import React from 'react';
import If from "../../components/utils/If";
import cn from "classnames";
import IconLeft from "../../components/icon/IconLeft";
import {createUseStyles} from "react-jss";
import IconRingSpinner from "../../components/icon/IconRingSpinner";

type Props = {
  page: number;
  pageSize: number;
  pageCount: number;
  pageButtons: number[];
  rowsPerPageOptions: number[];
  onChangeRowsPerPage: (rowsPerPage: number) => void;
  total: number;
  canNextPage: boolean;
  canPreviousPage: boolean;
  onPageChange: (page: number) => void;
  loading: boolean;
};

export default function TablePaginator({
                                         page,
                                         pageSize,
                                         pageCount,
                                         total,
                                         rowsPerPageOptions,
                                         onChangeRowsPerPage,
                                         pageButtons,
                                         canNextPage,
                                         canPreviousPage,
                                         onPageChange,
                                         loading
                                       }: Props) {
  const showArrows = pageCount > 5;
  const showPaginator = pageButtons.length > 1 || total < page * pageSize;
  const position = (index: number) => {
    if (index === 0) {
      return 'left';
    }
    if (index === pageCount - 1 && !showArrows) {
      return 'right';
    }
    return 'center';
  };

  return (
    <div className="flex flex-row items-center justify-between ml-2 mt-6">
      <div className="flex flex-row items-center">
        <If condition={showArrows}>
        <span className="cursor-pointer font-bold" onClick={() => canPreviousPage && onPageChange(page - 1)}>
          <IconLeft className={cn("mr-6 font-bold", {"text-gray": !canPreviousPage})}/>
        </span>
        </If>
        <div className="flex flex-row">
          {showPaginator && pageButtons.map((x, index) =>
            <PaginatorButton value={x} position={position(index)} key={index} loading={loading}
                             index={index} activeValue={page} handleClick={onPageChange}/>
          )}
          <If condition={showArrows}>
            <div id={`radiogroup-option-selector`} tabIndex={5} role="radio" aria-checked="true"
                 className={cn(
                   'group relative bg-white text-center rounded-r-md text-gray-dark',
                 )}>
              <div
                className={cn('border border-gray px-6 py-1.5 rounded-r-md border-l-0',)}>
                <input type="text"
                       className="border-0 border-b border-gray-dark h-6 w-14 text-center p-0 -mb-0.5"
                       onChange={(e) => onPageChange(Number(e.currentTarget.value) - 1)}/> / {pageCount}
              </div>
            </div>
          </If>
        </div>
        <If condition={showArrows}>
        <span className="cursor-pointer font-bold" onClick={() => canNextPage && onPageChange(page + 1)}>
          <IconLeft className={cn("ml-6  font-bold transform rotate-180", {"text-gray": !canNextPage})}/>
        </span>
        </If>
      </div>
      <div>
      <span className="text-gray-dark ml-5">
        Showing {pageSize * (page + 1) - pageSize + 1} to {pageSize * (page + 1)} of {total} entries
      </span>
        <span className="text-gray-dark ml-5">
        <select onChange={(e) => onChangeRowsPerPage(Number(e.currentTarget.value))}
                className="border border-gray rounded-md py-1.5" defaultValue={pageSize}>
          {rowsPerPageOptions.map((x, index) =>
            <option key={index} value={x}>{x}</option>
          )}
        </select>
      </span>
      </div>
    </div>
  )
}


type PaginatorButtonProps = {
  value: number
  position: "left" | "right" | "center"
  index: number
  activeValue: number
  handleClick: (value: number) => void
  loading: boolean
}

function PaginatorButton({
                           value, position, index, activeValue, handleClick, loading
                         }: PaginatorButtonProps) {
  const active = value === activeValue;

  const styles = createUseStyles({
    radioButton: {
      minWidth: '30px',
    },
  })();

  return (
    <div id={`radiogroup-option-${value}`} tabIndex={index} role="radio" aria-checked="true"
         onClick={() => handleClick(value)}
         className={cn(
           'group relative cursor-pointer bg-white text-center',
           {'rounded-l-md': position === 'left'},
           {'rounded-r-md': position === 'right'},
           {'bg-blue text-white': active},
           styles.radioButton,
         )}>
      <div
        className={cn(
          'border border-gray px-6 py-1.5',
          {'group-hover:bg-blue-light': !active},
          {'rounded-l-md': position === 'left'},
          {'border-l-0': position !== 'left'},
          {'rounded-r-md': position === 'right'},
        )}>
        {loading && active && <IconRingSpinner className=""/>}
        {(!loading || !active) && <>{value + 1}</>}
      </div>
    </div>
  );
}
